import { usePubNub } from 'pubnub-react';
import React, { ReactElement, useEffect, useState } from 'react';
import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  DEBOUNCE_SEND_MESSAGE_ACTIVE,
  MESSAGE_KEY,
  MESSAGE_SIZE,
  TEXT_STRING,
  SIGNAL_EVENT,
  queryKeys,
  routes,
  QUICK_REPLY_ITEMS,
} from '../../constants';
import {
  FileEvent,
  MessageActionEvent,
  MessageEvent,
  SignalEvent,
} from 'pubnub';
import { useMessageStore, useOrderStore, useUserStore } from 'store';
import {
  EChannelType,
  EGiftPaymentStep,
  EOrderStep,
  EPointPaymentStep,
  EUserProvider,
  TBodyPushNotification,
  TChannel,
  TChannelDetail,
  TGiftMessage,
  TMember,
  TMessageAction,
  TMessageActions,
  TMessageDetail,
  TQuickReplyItem,
  TSendLineChatMessage,
  TSendMessage,
  TSendQuickReplyAnswerBody,
} from 'types';
import {
  pushNotificationMessage,
  sendQuickReplyAnswer,
  updateClientSeenTime,
  updateSeenTime,
  sendLineChatMessage,
} from 'api';
import { useChannelActivity } from 'hooks/useChannelActivity';
import { useNavigate } from 'react-router-dom';
import { FloatButton, GiftView, MaleCard, Modal } from 'components';
import { ChatContext } from './context';
import PaymentRequest from './PaymentRequest';
import QuickReplyChips from './QuickReplyChips';
import {
  QUICK_REPLY_ANSWER_FAIL_TIMEOUT_MS,
  canHideQuickReplyTyping,
  isMatchingQuickReplyAnswerEvent,
  isQuickReplyTapLocked,
  shouldStopAnsweringAfterQuickReplyFailure,
  loadQuickReplySession,
  markQuickReplyConsumed,
  remainingQuickReplyTypingHideMs,
  selectRemainingQuickReplies,
  trackQuickReplyClick,
} from 'utils';
import { sendChatMessage, submitQuickReplyQuestion } from 'utils/chatSend';

type TMessage = {
  channel: string;
  message: TMessageDetail;
  timetoken: string | number;
  messageType?: string | number | undefined;
  uuid?: string | undefined;
  error?: string | undefined;
  actions?: TMessageActions;
};

type Props = {
  channel: string;
  users?: TMember[];
  onCreateChannel?: () => Promise<TChannel | undefined>;
  channelData?: TChannelDetail;
  onBlur?: () => void;
  onFocus?: () => void;
  action?: ReactElement;
  refetch: () => void;
  refetchChannelDetail: () => void;
  readonly?: boolean;
};

// When closing MaleCard via an outside tap, that same tap can reach the chat
// underneath; swallow subsequent events in the capture phase until click/touchend.
function useDismissSelectedMaleOnOutsideTap(
  selectedMale: TMember | undefined,
  setSelectedMale: React.Dispatch<React.SetStateAction<TMember | undefined>>,
) {
  const selectedMaleRef = React.useRef<TMember | undefined>(selectedMale);
  selectedMaleRef.current = selectedMale;
  const swallowingTapRef = React.useRef(false);

  useEffect(() => {
    const isOutsideModal = (target: EventTarget | null): boolean => {
      if (!(target instanceof Node)) return true;
      const panels = document.querySelectorAll<HTMLElement>(
        '[id^="headlessui-dialog-panel-"]',
      );
      return !Array.from(panels).some((panel) => panel.contains(target));
    };

    let resetTimer: ReturnType<typeof setTimeout> | undefined;
    const stopSwallowing = () => {
      swallowingTapRef.current = false;
      if (resetTimer) {
        clearTimeout(resetTimer);
        resetTimer = undefined;
      }
    };

    const handler = (event: Event) => {
      if (swallowingTapRef.current) {
        event.stopImmediatePropagation();
        if (event.type === 'click' || event.type === 'touchend') {
          stopSwallowing();
        }
        return;
      }
      if (!selectedMaleRef.current) return;
      if (!isOutsideModal(event.target)) return;

      swallowingTapRef.current = true;
      resetTimer = setTimeout(stopSwallowing, 500);
      event.stopImmediatePropagation();
      setSelectedMale(undefined);
    };

    const eventNames: (keyof WindowEventMap)[] = [
      'pointerdown',
      'mousedown',
      'click',
      'touchend',
    ];
    eventNames.forEach((name) =>
      window.addEventListener(name, handler, { capture: true }),
    );
    return () => {
      eventNames.forEach((name) =>
        window.removeEventListener(name, handler, { capture: true }),
      );
      if (resetTimer) clearTimeout(resetTimer);
    };
  }, [setSelectedMale]);
}

const Chat = ({
  channel,
  channelData,
  users = [],
  onCreateChannel,
  onBlur,
  onFocus,
  action,
  refetch,
  refetchChannelDetail,
  readonly = false,
}: Props) => {
  // Hooks
  const navigate = useNavigate();
  const pubnub = usePubNub();
  const { user, isAuthenticated, clientId } = useUserStore();
  const { resetUnreadCount } = useMessageStore();
  const { setOrderStep, clearSettingOrderMeta } = useOrderStore();
  const timeoutSeenRef = React.useRef<NodeJS.Timeout>();
  const answeringRef = React.useRef<{
    quickReplyId: string;
    startedAtMs: number;
    eventReceived: boolean;
  } | null>(null);
  const answeringHideTimeoutRef = React.useRef<NodeJS.Timeout>();
  const answeringFailTimeoutRef = React.useRef<NodeJS.Timeout>();
  const quickReplyInFlightRef = React.useRef(false);
  const queryClient = useQueryClient();

  // States
  const [channels] = useState<string[]>([channel]);
  const [messages, setMessages] = useState<TMessage[]>([]);
  const [showTyping, setShowTyping] = useState<boolean>(false);
  const [giftView, setGiftView] = useState<TGiftMessage | undefined>();
  const [, setGiftViewQueue] = useState<TGiftMessage[]>([]);
  const [historyPaymentId, setHistoryPaymentId] = useState<number>();
  const [stepPaymentGift, setStepPaymentGift] =
    React.useState<EGiftPaymentStep>();
  const [pointHistoryId, setPointHistoryId] = useState<number>();
  const [paymentStep, setPaymentStep] = useState<EPointPaymentStep>();
  const [selectedMale, setSelectedMale] = useState<TMember>();
  const [consumedQuickReplyIds, setConsumedQuickReplyIds] = useState<string[]>(
    () => loadQuickReplySession(channel).consumedIds,
  );
  const [isQuickReplyResponding, setIsQuickReplyResponding] =
    useState<boolean>(false);
  const [isAnswering, setIsAnswering] = useState<boolean>(false);

  useDismissSelectedMaleOnOutsideTap(selectedMale, setSelectedMale);

  const isPrivateChat = React.useMemo(
    () => channelData?.type === EChannelType.PRIVATE,
    [channelData?.type],
  );

  // Query
  const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [queryKeys.MESSAGE_LIST, channel],
    queryFn: async () => {
      return pubnub
        .fetchMessages({
          channels: [channel],
          count: MESSAGE_SIZE,
          includeMessageType: true,
          includeUUID: true,
          includeMeta: true,
          includeMessageActions: true,
          stringifiedTimeToken: true,
          start: messages[messages.length - 1]?.timetoken,
        })
        .then((res) => res.channels[channel] || []);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam, allPageParams) => {
      return lastPage.length >= MESSAGE_SIZE ? lastPageParam + 1 : undefined;
    },
    gcTime: 0,
    enabled: !!pubnub,
  });

  const femaleParticipant = React.useMemo(
    () =>
      users.find(
        (member) =>
          member.provider === EUserProvider.FEMALE_APPLICATION && isPrivateChat,
      ),
    [isPrivateChat, users],
  );

  const { scheduleNormal, flushQuestion } = useChannelActivity({
    channelId: channel,
    isAuthenticated,
    clientId: clientId || undefined,
    debounceMs: DEBOUNCE_SEND_MESSAGE_ACTIVE,
  });

  const { mutateAsync: mutateSeen } = useMutation({
    mutationFn: () => {
      return isAuthenticated
        ? updateSeenTime(channel)
        : updateClientSeenTime(channel, clientId || '');
    },
    retry: 0,
  });

  const { mutate: mutatePushNotification } = useMutation({
    mutationFn: (body: TBodyPushNotification) => {
      return pushNotificationMessage(body);
    },
    retry: 0,
  });

  const { mutate: mutateLineChatMessage } = useMutation({
    mutationFn: ({
      senderName,
      channelId,
      channelType,
      channelParticipant,
    }: TSendLineChatMessage) => {
      return sendLineChatMessage({
        senderName,
        channelId,
        channelType,
        channelParticipant,
      });
    },
    retry: 0,
  });

  const { mutateAsync: mutateSendQuickReplyAnswer } = useMutation({
    mutationFn: (payload: TSendQuickReplyAnswerBody) => {
      return sendQuickReplyAnswer(payload);
    },
    retry: 0,
  });

  // Memo, callbacks
  const femaleId = React.useMemo(
    () => femaleParticipant?.id,
    [femaleParticipant?.id],
  );

  // Answer text/media are server-authored from quickReplyId — no client prefetch needed.
  const isQuickReplyBlocked = React.useMemo(
    () => !femaleId || isQuickReplyResponding || isAnswering,
    [femaleId, isQuickReplyResponding, isAnswering],
  );

  const remainingQuickReplies = React.useMemo(
    () => selectRemainingQuickReplies(QUICK_REPLY_ITEMS, consumedQuickReplyIds),
    [consumedQuickReplyIds],
  );

  const myPublisherUuid = React.useMemo(
    () => user?.uuid || clientId || '',
    [clientId, user?.uuid],
  );

  const refetchCurrentUser = React.useCallback(() => {
    queryClient.refetchQueries({ queryKey: [queryKeys.CURRENT_USER] });
  }, [queryClient]);

  const mapMessage = React.useCallback(
    (message: TMessageDetail) => {
      let url;
      if (message.message.file) {
        url = pubnub.getFileUrl({
          channel: channel,
          id: message.message.file.id,
          name: message.message.file.name,
        });
      }
      return {
        ...message,
        url: url,
      };
    },
    [pubnub, channel],
  );

  const handleSignal = React.useCallback(
    (params: SignalEvent) => {
      const isMySignal = [clientId, user?.uuid].includes(params.publisher);
      if (params.channel === channel && !isMySignal) {
        const message = params.message;
        switch (message.event || message) {
          case SIGNAL_EVENT.TYPING_ON:
            setShowTyping(true);
            break;
          case SIGNAL_EVENT.TYPING_OFF:
            setShowTyping(false);
            break;
          case SIGNAL_EVENT.DELETE_MESSAGE:
            setMessages((prev) =>
              prev.filter(
                (msg) => String(message.timetoken) !== String(msg.timetoken),
              ),
            );
            break;
          case SIGNAL_EVENT.REVIEWED_MEMBER:
          case SIGNAL_EVENT.DISBAND_MEMBER:
          case SIGNAL_EVENT.PROJECT_INVITED:
            refetchChannelDetail();
            break;
          case SIGNAL_EVENT.PROJECT_ACCOUNTING_COMPLETED:
            refetch();
            break;
          case SIGNAL_EVENT.PROJECT_PENDING_REVIEW:
            refetchCurrentUser();
            break;
          default:
            break;
        }
      }
    },
    [
      setShowTyping,
      user,
      clientId,
      channel,
      refetch,
      refetchChannelDetail,
      refetchCurrentUser,
    ],
  );

  const handleViewGift = React.useCallback(
    async (gift: TGiftMessage) => {
      if (giftView) {
        setGiftViewQueue((prev) => [...prev, gift]);
      } else setGiftView(gift);
    },
    [giftView],
  );

  const handleSeenTime = React.useCallback(() => {
    // send seen time
    if (timeoutSeenRef.current) return;
    timeoutSeenRef.current = setTimeout(() => {
      mutateSeen();
      resetUnreadCount(channel);
      clearTimeout(timeoutSeenRef.current);
      timeoutSeenRef.current = undefined;
    }, DEBOUNCE_SEND_MESSAGE_ACTIVE);
  }, [channel, resetUnreadCount, mutateSeen]);

  const clearAnsweringTimers = React.useCallback(() => {
    if (answeringHideTimeoutRef.current) {
      clearTimeout(answeringHideTimeoutRef.current);
      answeringHideTimeoutRef.current = undefined;
    }
    if (answeringFailTimeoutRef.current) {
      clearTimeout(answeringFailTimeoutRef.current);
      answeringFailTimeoutRef.current = undefined;
    }
  }, []);

  const stopAnswering = React.useCallback(() => {
    clearAnsweringTimers();
    answeringRef.current = null;
    setIsAnswering(false);
  }, [clearAnsweringTimers]);

  const startAnswering = React.useCallback(
    (quickReplyId: string) => {
      clearAnsweringTimers();
      answeringRef.current = {
        quickReplyId,
        startedAtMs: Date.now(),
        eventReceived: false,
      };
      setIsAnswering(true);
      answeringFailTimeoutRef.current = setTimeout(() => {
        answeringFailTimeoutRef.current = undefined;
        stopAnswering();
      }, QUICK_REPLY_ANSWER_FAIL_TIMEOUT_MS);
    },
    [clearAnsweringTimers, stopAnswering],
  );

  const tryHideAnsweringAfterEvent = React.useCallback(() => {
    const pending = answeringRef.current;
    if (!pending?.eventReceived) return;
    if (
      canHideQuickReplyTyping({
        eventReceived: true,
        startedAtMs: pending.startedAtMs,
      })
    ) {
      stopAnswering();
      return;
    }
    if (answeringHideTimeoutRef.current) return;
    answeringHideTimeoutRef.current = setTimeout(() => {
      answeringHideTimeoutRef.current = undefined;
      if (answeringRef.current?.eventReceived) {
        stopAnswering();
      }
    }, remainingQuickReplyTypingHideMs(pending.startedAtMs));
  }, [stopAnswering]);

  const handleMessage = React.useCallback(
    (event: MessageEvent) => {
      if (channel === event.channel) {
        if (event?.message?.gift) {
          handleViewGift(event?.message?.gift);
        }
        handleSeenTime();
        setMessages((messages) => {
          return messages.some(
            (message) => message.timetoken === event.timetoken,
          )
            ? messages
            : [
                mapMessage({
                  channel: event.channel,
                  message: event.message,
                  timetoken: event.timetoken,
                  uuid: event.publisher,
                }),
                ...messages,
              ];
        });

        if (
          isMatchingQuickReplyAnswerEvent(
            event.message,
            answeringRef.current?.quickReplyId ?? null,
          )
        ) {
          if (answeringRef.current) {
            answeringRef.current = {
              ...answeringRef.current,
              eventReceived: true,
            };
          }
          tryHideAnsweringAfterEvent();
        }

        if (
          [MESSAGE_KEY.ADMIN_RELEASE_PAYMENT].includes(
            event?.message?.data?.key,
          )
        ) {
          refetchCurrentUser();
          refetch();
        }

        if (
          [MESSAGE_KEY.GIRL_MEETING, MESSAGE_KEY.GIRL_CANCEL_MEETING].includes(
            event?.message?.data?.key,
          )
        ) {
          refetch();
        }
      }
    },
    [
      channel,
      handleSeenTime,
      mapMessage,
      refetch,
      refetchCurrentUser,
      handleViewGift,
      tryHideAnsweringAfterEvent,
    ],
  );

  const handleFile = React.useCallback(
    (event: FileEvent) => {
      handleSeenTime();
      setMessages((messages) => [
        mapMessage({
          channel: event.channel,
          message: { text: event.message.text, file: event.file },
          timetoken: event.timetoken,
          uuid: event.publisher,
        }),
        ...messages,
      ]);
    },
    [handleSeenTime, mapMessage],
  );

  const sendMessage = React.useCallback(
    async (payload: TSendMessage) => {
      await sendChatMessage({
        payload,
        pubnub,
        channel,
        channelType: channelData?.type,
        userName: user?.name,
        userUuid: user?.uuid,
        users,
        onCreateChannel,
        mutatePushNotification,
        mutateLineChatMessage,
        scheduleNormal,
        flushQuestion,
      });
    },
    [
      users,
      user,
      channel,
      channelData?.type,
      onCreateChannel,
      pubnub,
      scheduleNormal,
      flushQuestion,
      mutatePushNotification,
      mutateLineChatMessage,
    ],
  );

  const onTyping = React.useCallback(
    (status: boolean) => {
      pubnub.signal({
        message: status ? SIGNAL_EVENT.TYPING_ON : SIGNAL_EVENT.TYPING_OFF,
        channel: channel,
      });
    },
    [pubnub, channel],
  );

  const handleQuickReplySelect = React.useCallback(
    async (item: TQuickReplyItem) => {
      if (
        !isPrivateChat ||
        isQuickReplyBlocked ||
        isQuickReplyTapLocked(
          quickReplyInFlightRef.current,
          answeringRef.current?.quickReplyId ?? null,
        ) ||
        consumedQuickReplyIds.includes(item.id) ||
        !myPublisherUuid ||
        !femaleId
      ) {
        return;
      }

      quickReplyInFlightRef.current = true;
      setIsQuickReplyResponding(true);
      trackQuickReplyClick({
        questionId: item.id,
        userId: user?.id,
      });

      try {
        await submitQuickReplyQuestion({
          item,
          sendMessage,
          startAnswering,
          sendAnswer: () =>
            mutateSendQuickReplyAnswer({
              channelId: channel,
              quickReplyId: item.id,
              clientId: isAuthenticated ? undefined : clientId || undefined,
            }),
        });
        markQuickReplyConsumed(channel, item.id);
        setConsumedQuickReplyIds((prev) =>
          prev.includes(item.id) ? prev : [...prev, item.id],
        );
      } catch {
        if (
          shouldStopAnsweringAfterQuickReplyFailure(
            answeringRef.current?.quickReplyId ?? null,
            item.id,
          )
        ) {
          stopAnswering();
        }
      } finally {
        quickReplyInFlightRef.current = false;
        setIsQuickReplyResponding(false);
      }
    },
    [
      channel,
      clientId,
      consumedQuickReplyIds,
      femaleId,
      isAuthenticated,
      isPrivateChat,
      isQuickReplyBlocked,
      myPublisherUuid,
      sendMessage,
      user?.id,
      mutateSendQuickReplyAnswer,
      startAnswering,
      stopAnswering,
    ],
  );

  const handleRedirectChannel = React.useCallback(
    async (member: TMember) => {
      if (member.provider === EUserProvider.MALE_APPLICATION) {
        setSelectedMale(member);
        return;
      }

      if (
        ![EChannelType.MALE_MANAGEMENT].includes(
          channelData?.type as EChannelType,
        )
      ) {
        if (member.provider === EUserProvider.FEMALE_APPLICATION && member.id) {
          navigate(routes.DETAIL_FEMALE.replace(':id', `${member.id}`));
        } else if (
          ![
            EUserProvider.FEMALE_APPLICATION,
            EUserProvider.MALE_APPLICATION,
          ].includes(member.provider)
        ) {
          navigate(
            routes.CONVERSATION.replace(':id', `${user?.managementChannelId}`),
          );
        }
      }
    },
    [channelData?.type, navigate, user?.managementChannelId],
  );

  const handleMessageAction = React.useCallback((event: MessageActionEvent) => {
    const { messageTimetoken, type, value, uuid, actionTimetoken } = event.data;

    if (type !== 'updated' || !value) return;

    const newAction: TMessageAction = {
      uuid,
      value,
      actionTimetoken,
      type,
    };

    setMessages((prev) =>
      prev.map((msg) => {
        if (String(msg.timetoken) !== String(messageTimetoken)) return msg;

        return {
          ...msg,
          message: {
            ...msg.message,
            text: value,
          },
          actions: {
            ...(msg.actions || {}),
            updated: [newAction],
          },
        };
      }),
    );
  }, []);

  // Effects
  useEffect(() => {
    if (data?.pages) {
      const reverseData = [...data.pages[data.pages.length - 1]].reverse();
      setMessages((prev) => [...prev, ...reverseData.map(mapMessage)]);
    }
    return () => {};
  }, [data, mapMessage]);

  useEffect(() => {
    const listenerParams = {
      message: handleMessage,
      signal: handleSignal,
      file: handleFile,
      messageAction: handleMessageAction,
    };

    if (!isLoading) {
      pubnub.addListener(listenerParams);
      pubnub.subscribe({ channels });

      return () => {
        pubnub.unsubscribe({ channels });
        pubnub.removeListener(listenerParams);
      };
    }
  }, [
    pubnub,
    channels,
    handleMessage,
    handleSignal,
    handleFile,
    isLoading,
    handleMessageAction,
  ]);

  useEffect(() => {
    return () => {
      clearAnsweringTimers();
      answeringRef.current = null;
    };
  }, [clearAnsweringTimers]);

  useEffect(() => {
    resetUnreadCount(channel);

    return () => {};
  }, [channel, resetUnreadCount]);

  const memberObj = React.useMemo(() => {
    let obj: { [x: string]: TMember } = {};
    if (users.length) {
      users.forEach((user) => {
        obj[user.uuid] = user;
      });
    }
    return obj;
  }, [users]);

  return (
    <ChatContext.Provider
      value={{
        giftView,
        setGiftView: handleViewGift,
        historyPaymentId,
        setHistoryPaymentId,
        setStepPaymentGift,
        stepPaymentGift,
        setPointHistoryId,
        setPaymentStep,
      }}
    >
      <div className="h-full w-full fixed top-0 flex flex-col justify-between overflow-hidden">
        <ChatHeader
          channelData={channelData}
          users={users}
          onClick={handleRedirectChannel}
        />
        <MessageList
          isLoading={isLoading}
          messages={messages}
          fetchMore={fetchNextPage}
          hasMore={hasNextPage}
          members={memberObj}
          channelData={channelData}
          onClickAvatar={handleRedirectChannel}
          showQuickReplyTyping={isAnswering}
          quickReplyTypingAvatarUrl={femaleParticipant?.avatar}
        />
        {!readonly && (
          <>
            {isPrivateChat &&
              !!myPublisherUuid &&
              !!femaleParticipant?.uuid &&
              remainingQuickReplies.length > 0 && (
                <QuickReplyChips
                  items={remainingQuickReplies}
                  disabled={isQuickReplyBlocked}
                  onSelect={handleQuickReplySelect}
                />
              )}
            <MessageInput
              sendMessage={sendMessage}
              onTyping={onTyping}
              showTyping={showTyping}
              onBlur={onBlur}
              onFocus={onFocus}
              femaleId={femaleId}
              channelData={channelData}
            />
            {action}
          </>
        )}
        {channelData?.type === EChannelType.MALE_MANAGEMENT ? (
          <FloatButton
            className="justify-center"
            content={TEXT_STRING.NAVIGATION.CALL}
            handleClick={() => {
              // Same reset as Navbar CALL: do not carry over setting-order meta
              // from a prior "Play with this girl" session.
              clearSettingOrderMeta();
              setOrderStep(EOrderStep.ORDER_METHOD);
              navigate(routes.CALL);
            }}
          />
        ) : null}
      </div>
      {giftView && (
        <div className="fixed z-50 w-full h-full content-center">
          <GiftView
            name={giftView.name}
            loop={false}
            animations={giftView.animations}
            image={giftView.image}
            onComplete={() => {
              setGiftViewQueue((prev) => {
                setGiftView(prev[0]);
                return prev.slice(1);
              });
            }}
          />
        </div>
      )}

      <PaymentRequest
        paymentStep={paymentStep}
        setPaymentStep={setPaymentStep}
        pointHistoryId={pointHistoryId}
      ></PaymentRequest>
      <Modal
        open={!!selectedMale}
        onClose={() => setSelectedMale(undefined)}
        className="bg-white px-3 py-4"
        w="w-[calc(100%-12px)]"
      >
        <div className="flex flex-col gap-4">
          {selectedMale ? (
            <MaleCard
              name={selectedMale.name}
              avatar={selectedMale.avatar}
              biography={selectedMale.biography}
              ranking={selectedMale.ranking}
              isSalonMember={selectedMale.isSalonMember}
              isSalonPremiumMember={selectedMale.isSalonPremiumMember}
            />
          ) : null}
        </div>
      </Modal>
    </ChatContext.Provider>
  );
};

export default Chat;
